import { DeviceOS } from '../devices';
import { Url } from '../common';
import { MobileRoutes } from '../mobile';

export enum PushNotificationType {
    InstagramPostReady = 1,
    AgencyCustomMessage,
    VideoDownloadReady,
    VideoPublishedExternally,
    VideoPublishFailed,
    ImagePublishFailed,
    EmailPublishSuccess,
    EmailPublishFailed,
}

interface InstagramPostReadyPushNotificationData {
    type: PushNotificationType.InstagramPostReady;
    url: Url;
}

interface AgencyCustomMessagePushNotificationData {
    type: PushNotificationType.AgencyCustomMessage;
    url?: Url;
}

interface VideoDownloadReadyPushNotificationData {
    type: PushNotificationType.VideoDownloadReady;
    url: Url;
    executionId: string;
}

interface VideoPublishedExternallyPushNotificationData {
    type: PushNotificationType.VideoPublishedExternally;
    url: Url;
    executionId: string;
}

interface VideoPublishFailedPushNotificationData {
    type: PushNotificationType.VideoPublishFailed;
    url?: Url; // TODO: implement URL
    executionId: string;
}

interface ImagePublishFailedPushNotificationData {
    type: PushNotificationType.ImagePublishFailed;
    url?: Url;
    executionId: string;
}

interface EmailPublishSuccessPushNotificationDataLegacy {
    type: PushNotificationType.EmailPublishSuccess;
    url?: Url;
    executionId: string;
}

interface EmailPublishSuccessPushNotificationData {
    type: PushNotificationType.EmailPublishSuccess;
    url?: Url;
    scheduledEmailItemId: string;
}

interface EmailPublishFailedPushNotificationData {
    type: PushNotificationType.EmailPublishFailed;
    url?: Url;
    executionId: string;
}

export type PushNotificationData =
    | AgencyCustomMessagePushNotificationData
    | EmailPublishFailedPushNotificationData
    | EmailPublishSuccessPushNotificationData
    | EmailPublishSuccessPushNotificationDataLegacy
    | ImagePublishFailedPushNotificationData
    | InstagramPostReadyPushNotificationData
    | VideoDownloadReadyPushNotificationData
    | VideoPublishedExternallyPushNotificationData
    | VideoPublishFailedPushNotificationData;

/**
 * Required for mobile app v2.1 and earlier.
 * Can be removed once the old version is no longer required.
 */
interface LegacyGCMPushNotification {
    type: PushNotificationType;
    url?: string;
}

interface CreateGCMPushNotification {
    data: LegacyGCMPushNotification & {
        data: PushNotificationData;
        message: string;
        title?: string;
    };
}

type CreateAPNSPushNotification = PushNotificationData & {
    aps: {
        alert: string;
        title?: string;
    };
};

interface CreatePushNotificationData {
    title?: string;
    message: string;
    data: PushNotificationData;
}

export interface HasCreatePushNotificationData {
    readonly notification: CreatePushNotificationData;
}

export class PushNotificationMessageFactory implements HasCreatePushNotificationData {
    public constructor(public readonly notification: CreatePushNotificationData) {}

    public getSNSMessage(os: DeviceOS, messageKeys: { androidKey: string; iosKey: string }): Record<string, any> {
        const message: { [key: string]: any } = {};
        switch (os) {
            case 'android':
                // eslint-disable-next-line no-case-declarations
                const androidPayload: CreateGCMPushNotification = {
                    data: {
                        data: {
                            ...this.notification.data,
                        },
                        message: this.notification.message,
                        title: this.notification.title,
                        // TODO: remove these once the new mobile app version has been deployed. (higher than v2.1)
                        type: this.notification.data.type,
                        url: this.notification.data.url,
                    },
                };
                message[messageKeys.androidKey] = JSON.stringify(androidPayload);
                break;
            case 'ios':
                // eslint-disable-next-line no-case-declarations
                const iosPayload: CreateAPNSPushNotification = {
                    aps: {
                        alert: this.notification.message,
                        title: this.notification.title,
                    },
                    ...this.notification.data,
                };
                message[messageKeys.iosKey] = JSON.stringify(iosPayload);
                break;
            default:
                break;
        }

        return message;
    }
}

export function instagramPostReadyPushNotification(
    translatedMessage: string,
    scheduledPostKey: string,
): PushNotificationMessageFactory {
    return new PushNotificationMessageFactory({
        data: {
            type: PushNotificationType.InstagramPostReady,
            url: MobileRoutes.instagramPostReady.constructUrlWithQueryString({
                params: {
                    key: scheduledPostKey,
                },
                query: {},
            }),
        },
        message: translatedMessage,
    });
}

export function agencyCustomPushNotification(
    translatedMessage: string,
    inAppNotificationId: number,
): PushNotificationMessageFactory {
    return new PushNotificationMessageFactory({
        data: {
            type: PushNotificationType.AgencyCustomMessage,
            url: MobileRoutes.notification.constructUrlWithQueryString({
                params: {
                    inAppNotificationId,
                },
                query: {},
            }),
        },
        message: translatedMessage,
    });
}

export function videoDownloadReadyPushNotification(
    translatedMessage: string,
    videoUrl: Url,
    executionId: string,
): PushNotificationMessageFactory {
    return new PushNotificationMessageFactory({
        data: {
            executionId,
            type: PushNotificationType.VideoDownloadReady,
            url: MobileRoutes.videoDownloadReady.constructUrlWithQueryString({
                params: undefined,
                query: {
                    videoUrl,
                },
            }),
        },
        message: translatedMessage,
    });
}

export function videoPublishedExternallyPushNotification(
    translatedMessage: string,
    externalUrl: Url,
    executionId: string,
    inAppNotificationId?: number,
): PushNotificationMessageFactory {
    return new PushNotificationMessageFactory({
        data: {
            executionId,
            type: PushNotificationType.VideoPublishedExternally,
            // If an in-app notification is passed in, use that as URL instead of the external URL.
            url: inAppNotificationId
                ? MobileRoutes.notification.constructUrlWithQueryString({
                      params: {
                          inAppNotificationId,
                      },
                      query: {},
                  })
                : externalUrl,
        },
        message: translatedMessage,
    });
}

export function videoPublishFailedPushNotification(
    translatedMessage: string,
    executionId: string,
    inAppNotificationId: number,
): PushNotificationMessageFactory {
    return new PushNotificationMessageFactory({
        data: {
            executionId,
            type: PushNotificationType.VideoPublishFailed,
            url: MobileRoutes.notification.constructUrlWithQueryString({
                params: {
                    inAppNotificationId,
                },
                query: {},
            }),
        },
        message: translatedMessage,
    });
}

export function imagePublishFailedPushNotification(
    translatedMessage: string,
    executionId: string,
    inAppNotificationId: number,
): PushNotificationMessageFactory {
    return new PushNotificationMessageFactory({
        data: {
            executionId,
            type: PushNotificationType.ImagePublishFailed,
            url: MobileRoutes.notification.constructUrlWithQueryString({
                params: { inAppNotificationId },
                query: {},
            }),
        },
        message: translatedMessage,
    });
}

export function emailPublishSuccessPushNotification(
    translatedMessage: string,
    executionId: string,
    inAppNotificationId: number,
): PushNotificationMessageFactory {
    return new PushNotificationMessageFactory({
        data: {
            executionId,
            type: PushNotificationType.EmailPublishSuccess,
            url: MobileRoutes.notification.constructUrlWithQueryString({
                params: {
                    inAppNotificationId,
                },
                query: {},
            }),
        },
        message: translatedMessage,
    });
}

export function emailPublishFailedPushNotification(
    translatedMessage: string,
    executionId: string,
    inAppNotificationId: number,
): PushNotificationMessageFactory {
    return new PushNotificationMessageFactory({
        data: {
            executionId,
            type: PushNotificationType.EmailPublishFailed,
            url: MobileRoutes.notification.constructUrlWithQueryString({
                params: {
                    inAppNotificationId,
                },
                query: {},
            }),
        },
        message: translatedMessage,
    });
}
